type EnquiryType = "product" | "service";

const roundToTwoDecimals = (num: number): number =>
  Math.round(num * 100) / 100;

export const mapEnquiryToQuotationForm = (
  enquiryData: any,
  type: EnquiryType,
) => {
  const enquiryId =
    type === "product"
      ? enquiryData.enquiry_product_id || enquiryData.id
      : enquiryData.enquiry_service_id || enquiryData.id;

  const customerDetails = enquiryData.customer_details || {};
  const enquiryContacts =
    customerDetails.contact_details ||
    customerDetails.contacts ||
    [];

  const company = customerDetails.company_id
    ? {
        company_id: customerDetails.company_id,
        company_name: customerDetails.company_name || "",
        gstin: customerDetails.gstin || "",
        gst_type: customerDetails.gst_type || customerDetails.type || "",
        industry: customerDetails.industry || "",
        phone_number: customerDetails.phone_number || "",
        company_email: customerDetails.email || customerDetails.company_email || "",
        billing_address: customerDetails.billing_address || {},
        shipping_addresses:
          customerDetails.shipping_address ||
          customerDetails.shipping_addresses ||
          [],
        contacts: enquiryContacts.map((contact: any) => ({
          id: contact.contact_id || contact.id,
          contact_id: contact.contact_id || contact.id,
          name:
            contact.name ||
            contact.contact_person_name ||
            contact.contact_name,
          designation:
            contact.designation || contact.contact_person_designation,
          phone_country:
            contact.phone_country || contact.contact_person_phone_country,
          phone_number:
            contact.phone_number || contact.contact_person_phone_no,
          email: contact.email || contact.contact_person_email,
        })),
      }
    : null;

  const contact_persons = enquiryContacts.map((contact: any) => ({
    id: contact.contact_id || contact.id,
    name:
      contact.name || contact.contact_person_name || contact.contact_name,
    designation:
      contact.designation || contact.contact_person_designation,
    phone_country:
      contact.phone_country || contact.contact_person_phone_country,
    phone_number: contact.phone_number || contact.contact_person_phone_no,
    email: contact.email || contact.contact_person_email,
  }));

  const members = (enquiryData.assigned_members || []).map((member: any) => {
    const firstName = member.first_name || "";
    const lastName = member.last_name || "";
    const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    return {
      id: member.user_id,
      name:
        `${member.first_name || ""} ${member.last_name || ""}`.trim() ||
        member.username ||
        "",
      initials,
      first_name: firstName,
      last_name: lastName,
    };
  });

  const base = {
    enquiry_reference_number: enquiryData.enquiry_number || "",
    reference_number: enquiryData.reference_number || "",
    initiated_by: enquiryData.initiated_by || "",
    agent_name: enquiryData.agent_name || "",
    company,
    contact_persons,
    members,
    tags: Array.isArray(enquiryData.tags)
      ? enquiryData.tags.map((t: any) => String(t).trim()).filter(Boolean)
      : [],
    attachments: Array.isArray(enquiryData.attachments)
      ? enquiryData.attachments
          .map((attachment: any) =>
            typeof attachment === "string"
              ? attachment
              : attachment?.file_url || attachment?.url || "",
          )
          .filter(Boolean)
      : [],
    bill_from_address:
      enquiryData.bill_from &&
      typeof enquiryData.bill_from === "object" &&
      !Array.isArray(enquiryData.bill_from)
        ? enquiryData.bill_from
        : undefined,
    uid_number: enquiryData.uid_number || "",
  };

  if (type === "product") {
    const products = (enquiryData.enquiry_product_items || []).map(
      (item: any, index: number) => ({
        id: `enquiry-product-${item.enquiry_product_item_id || index}`,
        sr_no: index + 1,
        product_id: item.product_id,
        product_name: item.product_name,
        category_name: item.category_name,
        sub_category_name: item.sub_category_name,
        product_type: item.product_type || [],
        make: item.make_name,
        make_name: item.make_name,
        model: item.model_name,
        model_name: item.model_name,
        unit: item.unit_name,
        unit_name: item.unit_name,
        unit_price: parseFloat(item.unit_price || "0"),
        hsn_code: item.hsn_code,
        gst_percentage: parseFloat(item.gst_percentage || "0"),
        gst_value: 0,
        remark: item.remark || "",
        sku: item.product_sku,
        product_sku: item.product_sku,
        quantity: item.product_quantity ?? 1,
        product_quantity: item.product_quantity ?? 1,
        product_total: parseFloat(item.product_total || "0"),
        product_net_total: parseFloat(item.product_total || "0"),
        discount_type: "currency",
        discount_value: 0,
        serviceable: true,
        is_serviceable: true,
        discount: 0,
        net_value: parseFloat(item.product_total || "0"),
        total: parseFloat(item.product_total || "0"),
        shipping_address: null,
        rate_in_currency: parseFloat(item.unit_price || "0"),
        gross_value: parseFloat(item.product_total || "0"),
      }),
    );

    return {
      ...base,
      enquiry_product_id: enquiryId,
      products,
    };
  }

  const assigned_technician = (enquiryData.assigned_technician || []).map(
    (tech: any) => ({
      id: tech.user_id || tech.id,
      user_id: tech.user_id || tech.id,
      first_name: tech.first_name || "",
      last_name: tech.last_name || "",
      username: tech.username || "",
    }),
  );

  const services = (enquiryData.enquiry_service_items || []).map(
    (item: any, index: number) => {
      const unitPrice = parseFloat(item.service_rate || item.unit_price || "0");
      const quantity = item.product_quantity ?? 1;
      const gstPercent = parseFloat(item.gst_percentage || "0");
      const grossValue = roundToTwoDecimals(unitPrice * quantity);
      const netValue = grossValue;
      const gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);
      const total = roundToTwoDecimals(netValue + gstValue);

      return {
        id: `enquiry-service-${item.enquiry_service_item_id || index}`,
        sr_no: index + 1,
        product_id: item.product_id,
        product: item.product_name || item.service_name || "",
        product_name: item.product_name || item.service_name || "",
        service_category: item.service_category || "",
        service_method: item.service_method || "",
        service_type: item.service_type || "",
        unit_price: unitPrice,
        product_quantity: quantity,
        gst_percentage: gstPercent,
        gross_value: grossValue,
        net_value: netValue,
        gst_value: gstValue,
        total,
        discount_type: "currency",
        discount_value: 0,
        calculated_discount_value: 0,
      };
    },
  );

  return {
    ...base,
    enquiry_service_id: enquiryId,
    assigned_technician,
    services,
  };
};
